import { useEffect, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    FormGroup,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { UseMutateFunction } from '@tanstack/react-query';
import { z } from 'zod';

import ErrorMessage from '@components/ErrorMessage';
import Loader from '@components/Loader';
import { RefetchingIndicator } from '@components/RefetchingIndicator';
import { useGeneralPredefinedPrompts } from '@services/queries';
import { PromptGroups } from '@services/types';

import { generalPredefinedPromptsTexts } from '../../constants';
import { useOpenAccordion } from '../../hooks/useOpenAccordion';
import { GroupsWithPromptsRequest } from '../../services/types';
import { MenuOptionValue } from '../../types';

import { AccordionTitle } from './components/AccordionTitle';
import { AddButton } from './components/AddButton';
import { DeleteGroup } from './components/DeleteGroup';
import { PromptSection } from './components/PromptSection';

const PROMPTS_BY_GROUP_CONFIG = {
    group: {
        min: 1,
    },
    label: {
        min: 1,
    },
    text: {
        min: 1,
    },
};

const schema = z.object({
    groups: z.array(
        z.object({
            id: z.string().optional(),
            orderNumber: z.number(),
            group: z
                .string()
                .trim()
                .min(
                    PROMPTS_BY_GROUP_CONFIG.group.min,
                    generalPredefinedPromptsTexts.groupInputError
                ),
            prompts: z.array(
                z.object({
                    promptId: z.string().optional(),
                    label: z
                        .string()
                        .trim()
                        .min(
                            PROMPTS_BY_GROUP_CONFIG.label.min,
                            generalPredefinedPromptsTexts.labelInputError
                        ),
                    text: z
                        .string()
                        .trim()
                        .min(
                            PROMPTS_BY_GROUP_CONFIG.text.min,
                            generalPredefinedPromptsTexts.textInputError
                        ),
                })
            ),
        })
    ),
});

export type FormValues = z.infer<typeof schema>;

interface GeneralPredefinedPromptsFormProps {
    mutate: UseMutateFunction<void, Error, GroupsWithPromptsRequest>;
    isSending: boolean;
    data: PromptGroups;
}

const GeneralPredefinedPromptsForm = ({
    mutate,
    isSending,
    data,
}: GeneralPredefinedPromptsFormProps) => {
    const theme = useTheme();

    const { openAccordionId, handleAccordionChange } = useOpenAccordion();

    const defaultValues = useMemo(
        () => ({
            groups: data.map(({ prompts, group, id, orderNumber }) => ({
                id,
                group,
                orderNumber,
                prompts: prompts.map(({ label, text, promptId }) => ({
                    label,
                    text,
                    promptId,
                })),
            })),
        }),
        [data]
    );

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        control,
    } = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues,
        mode: 'all',
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'groups',
        keyName: 'groupFieldId',
    });

    //In the next version user will be able to drag and drop the groups to change the order
    //This is a temporary solution to get the highest order number
    const currentHighestOrderNumber = useMemo(
        () => Math.max(...fields.map(({ orderNumber }) => orderNumber)),
        [fields]
    );

    useEffect(() => {
        reset(defaultValues);
    }, [reset, defaultValues]);

    const onSubmit = ({ groups }: FormValues) => {
        mutate(groups);
    };

    return (
        <Stack
            component='form'
            id={MenuOptionValue.GeneralPredefinedPrompts}
            gap={2}
            onSubmit={handleSubmit(onSubmit)}
        >
            <Typography variant='body1'>{generalPredefinedPromptsTexts.formTitle}</Typography>
            <FormGroup>
                {fields.map(({ group, id: groupId, groupFieldId }, groupIndex) => (
                    <Accordion
                        key={groupFieldId}
                        expanded={openAccordionId === groupFieldId}
                        onChange={handleAccordionChange(groupFieldId)}
                        slotProps={{ transition: { unmountOnExit: true } }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`${group}-content`}
                            id={`${group}-header`}
                            sx={{ backgroundColor: theme.palette.customColors.lightGray }}
                            component='div'
                        >
                            <AccordionTitle
                                groupIndex={groupIndex}
                                errors={errors}
                                register={register}
                                isSending={isSending}
                            />
                        </AccordionSummary>
                        <AccordionDetails>
                            <PromptSection
                                errors={errors}
                                groupIndex={groupIndex}
                                register={register}
                                isSending={isSending}
                                groupId={groupId}
                                control={control}
                            />
                            <DeleteGroup
                                groupId={groupId}
                                groupIndex={groupIndex}
                                deleteDynamicField={remove}
                            />
                        </AccordionDetails>
                    </Accordion>
                ))}
            </FormGroup>
            <AddButton
                onAdd={() =>
                    append({ group: '', prompts: [], orderNumber: currentHighestOrderNumber + 1 })
                }
            />
        </Stack>
    );
};

interface GeneralPredefinedPromptsProps {
    mutate: UseMutateFunction<void, Error, GroupsWithPromptsRequest>;
    isSending: boolean;
}

export const GeneralPredefinedPrompts = ({ mutate, isSending }: GeneralPredefinedPromptsProps) => {
    const { data, isLoading, isError, error, isFetching } = useGeneralPredefinedPrompts();

    if (isLoading) return <Loader />;

    if (isError) return <ErrorMessage error={error} />;

    if (!data) return null;

    const isRefetching = !isLoading && isFetching;

    return (
        <>
            {isRefetching && <RefetchingIndicator />}
            <GeneralPredefinedPromptsForm mutate={mutate} isSending={isSending} data={data} />
        </>
    );
};
